import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Chip, Divider } from '@mui/material';
import { getVersionInfo, getBackendVersionInfo } from '../../utils/version';

interface VersionInfo {
  version: string;
  environment: string;
  gitCommit?: string;
  buildDate?: string;
  deploymentDate?: string;
  releaseUrl?: string;
}

interface BackendVersionInfo {
  version: string;
  environment: string;
  gitCommit?: string;
  buildDate?: string;
  deploymentDate?: string;
  error?: string;
}

const VersionDisplay: React.FC = () => {
  const [backendInfo, setBackendInfo] = useState<BackendVersionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const versionInfo: VersionInfo = {
    ...getVersionInfo(),
    releaseUrl: process.env.REACT_APP_RELEASE_URL,
  };

  useEffect(() => {
    const fetchBackendVersion = async () => {
      try {
        const backendData = await getBackendVersionInfo();
        setBackendInfo(backendData);
      } catch (error) {
        setBackendInfo({
          version: 'unknown',
          environment: 'unknown',
          error: 'Failed to fetch backend version'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBackendVersion();
  }, []);

  const getEnvironmentColor = (env: string) => {
    switch (env.toLowerCase()) {
      case 'production':
        return 'success';
      case 'staging':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Paper 
      elevation={2}
      sx={{
        padding: 3,
        margin: 2,
        maxWidth: 600,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <Typography variant="h4" gutterBottom align="center">
        Application Version
      </Typography>
      
      {/* Frontend Section */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
        Frontend
      </Typography>
      
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 1,
      }}>
        <Typography variant="body1" fontWeight="bold">
          Version:
        </Typography>
        <Typography variant="body1">
          {versionInfo.version}
        </Typography>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 1,
      }}>
        <Typography variant="body1" fontWeight="bold">
          Environment:
        </Typography>
        <Chip 
          label={versionInfo.environment.toUpperCase()} 
          color={getEnvironmentColor(versionInfo.environment) as any}
          size="small"
        />
      </Box>

      {versionInfo.gitCommit && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}>
          <Typography variant="body1" fontWeight="bold">
            Git Commit:
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {versionInfo.gitCommit}
          </Typography>
        </Box>
      )}

      {versionInfo.buildDate && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}>
          <Typography variant="body1" fontWeight="bold">
            Build Date:
          </Typography>
          <Typography variant="body2">
            {formatDate(versionInfo.buildDate)}
          </Typography>
        </Box>
      )}

      {versionInfo.deploymentDate && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}>
          <Typography variant="body1" fontWeight="bold">
            Deployment Date:
          </Typography>
          <Typography variant="body2">
            {formatDate(versionInfo.deploymentDate)}
          </Typography>
        </Box>
      )}

      {versionInfo.releaseUrl && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}>
          <Typography variant="body1" fontWeight="bold">
            Release URL:
          </Typography>
          <Typography variant="body2">
            <a href={versionInfo.releaseUrl} target="_blank" rel="noopener noreferrer">
              View Release
            </a>
          </Typography>
        </Box>
      )}

      {/* Backend Section */}
      <Divider sx={{ my: 3 }} />
      
      <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
        Backend
      </Typography>

      {loading ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}>
          <Typography variant="body1" fontWeight="bold">
            Status:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      ) : backendInfo?.error ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
        }}>
          <Typography variant="body1" fontWeight="bold">
            Status:
          </Typography>
          <Typography variant="body2" color="error">
            {backendInfo.error}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 1,
          }}>
            <Typography variant="body1" fontWeight="bold">
              Version:
            </Typography>
            <Typography variant="body1">
              {backendInfo?.version || 'Unknown'}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 1,
          }}>
            <Typography variant="body1" fontWeight="bold">
              Environment:
            </Typography>
            <Chip 
              label={(backendInfo?.environment || 'Unknown').toUpperCase()} 
              color={getEnvironmentColor(backendInfo?.environment || 'unknown') as any}
              size="small"
            />
          </Box>

          {backendInfo?.gitCommit && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 1,
            }}>
              <Typography variant="body1" fontWeight="bold">
                Git Commit:
              </Typography>
              <Typography variant="body2" fontFamily="monospace">
                {backendInfo.gitCommit}
              </Typography>
            </Box>
          )}

          {backendInfo?.buildDate && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 1,
            }}>
              <Typography variant="body1" fontWeight="bold">
                Build Date:
              </Typography>
              <Typography variant="body2">
                {formatDate(backendInfo.buildDate)}
              </Typography>
            </Box>
          )}

          {backendInfo?.deploymentDate && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 1,
            }}>
              <Typography variant="body1" fontWeight="bold">
                Deployment Date:
              </Typography>
              <Typography variant="body2">
                {formatDate(backendInfo.deploymentDate)}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default VersionDisplay; 